import { useEffect, useCallback, useState, useRef, KeyboardEvent } from 'react';
import { useLocation } from 'react-router';
import { createPopper, Instance, Placement } from '@popperjs/core';
import {
  childHasFocus,
  getFocusableElements,
  isActiveElement,
  isOrContainsTarget,
  isToggleCollapsibleButton,
} from '../lib/DomUtils/utils';
import { UseCollapsible } from './types/useCollapsibleOverlay';
import { GlobalDocumentIds } from '../types/GlobalDocumentIds';
import { KeyEvent } from '../types/KeyEvent';
import { useHtmlValidId } from './useHtmlValidId';

// react-resize-detector is ESM-exclusive.
// We use NodeJS's new require(esm) functionality here, see https://nodejs.org/en/blog/release/v22.12.0
// eslint-disable-next-line @typescript-eslint/no-require-imports
const { useResizeDetector } = require('react-resize-detector');

/**
 * Provides the base building blocks for an a11y conform collapsible component.
 *
 * A collapsible consists of a toggle and content component.
 *
 * Requirements are:
 *  - toggle and content components need to set className based on `isOpen` state
 *  - both have correct aria attributes
 *  - content renders an empty element b/c aria-controls need to point to a valid id
 *  - the clickOutside eventHandler is registered on `isOpen` and removed otherwise
 *  - content stays opened when clicked inside
 *  - content closes when clicked outside or ESC is hit
 *  - ESC needs to stop propagating
 *  - focus is handled properly
 *  - content shall be scrollable via SPACE
 *
 * TODO: if isActiveElement(collapsibleRef) and user hits Space in a
 * scrollable child container, the container should be scrolled before the
 * browser scrolls to the end of the page. This should be achieved by setting
 * tabIndex="-1" on the first child element (if scrollable) instead of the
 * collapsible itself.
 */
export default function useCollapsibleOverlay<TCollapsible extends HTMLElement = HTMLDivElement>(
  placement: Placement | null = null,
): UseCollapsible<TCollapsible> {
  const toggleRef = useRef<HTMLButtonElement>(null);
  const collapsibleRef = useRef<TCollapsible>(null);
  const lastKeyPressed = useRef<{ code: string; shiftKey: boolean }>(undefined);

  const [isOpen, setIsOpen] = useState(false);
  const toggleCollapsible = useCallback(() => setIsOpen(state => !state), []);
  const closeCollapsible = useCallback(() => {
    toggleRef.current?.focus();
    setIsOpen(state => (state ? false : state));
  }, []);
  const openCollapsible = useCallback(() => setIsOpen(state => state || true), []);

  // height and collapsibleRef.current.offsetHeight are same
  const { height: collapsibleRefHeight } = useResizeDetector({ targetRef: collapsibleRef });
  const location = useLocation();
  const id = useHtmlValidId();

  const focusToggle = (): void => {
    toggleRef.current?.focus();
  };

  const popperInstance = useRef<Instance>(undefined);

  /**
   * Closes the collapsible when user hits (Shift+) Tab or ESC.
   *
   * NOTE that the browsers default event flow goes like this:
   * 1. onKeyDown
   * 2. focus is set to next focusable element
   * 3. onKeyUp on the newly focused element is triggered
   *
   * Hence, when the user cycles through elements we must use the keydown event
   * to tackle (Shift+) Tab. We have to cover these cases:
   *
   * 1. Tab
   * - the last focusable element inside the collapsible has focus or
   * - the toggle component does not have focus (in case the collapsible does
   *   not have focusable elements)
   *
   * 2. Shift + Tab
   * - the toggle has focus
   */
  const onKeyDown = useCallback(
    (event: KeyboardEvent<any>): void => {
      lastKeyPressed.current = { code: event.code, shiftKey: event.shiftKey };

      if (isOpen && (event.code === KeyEvent.Tab || event.code === KeyEvent.Escape)) {
        if (collapsibleRef.current) {
          const focusableElements = getFocusableElements(collapsibleRef.current);

          if (event.code === KeyEvent.Escape) {
            // prevent default to avoid for example modal closing on escape
            event.preventDefault();
            event.stopPropagation();

            toggleRef.current?.focus();
            setIsOpen(false);
          } else if (
            toggleRef.current === document.activeElement &&
            event.code === KeyEvent.Tab &&
            (event.shiftKey || !focusableElements || focusableElements.length === 0)
          ) {
            // do not prevent default to allow browser to focus next element
            setIsOpen(false);
          } else {
            // lookup if focus is already in collapsible or not
            const focusedElementIndex = focusableElements?.findIndex(el => el === document.activeElement) ?? -1;

            if (event.code === KeyEvent.Tab && !event.shiftKey && focusedElementIndex === -1) {
              event.preventDefault();
              event.stopPropagation();

              // focus first element of collapsible when focus is on toggle
              focusableElements?.at(0)?.focus();
            }
          }
        }
      }
    },
    [isOpen],
  );

  const onOutsideClick = (event: MouseEvent) => {
    // click is on the toggling button -> don't close, onclick from button will handle this
    // click inside container -> don't close, valid click
    if (
      isActiveElement(collapsibleRef) ||
      isOrContainsTarget(collapsibleRef, event.target) ||
      isToggleCollapsibleButton(collapsibleRef, event)
    ) {
      return;
    }

    if (!isActiveElement(collapsibleRef) && !childHasFocus(collapsibleRef)) {
      setIsOpen(false);
    }
  };

  /**
   * Using onBlur Event in combination with keyboard events to set focus back on toggle button to avoid destroying input
   * functionality like setting date in datepicker using keyboard arrow or tab key.
   */
  useEffect(() => {
    if (isOpen && collapsibleRef.current) {
      const focusableElements = getFocusableElements(collapsibleRef.current);

      const setFocusToToggleFirstElement = (event: Event) => {
        if (lastKeyPressed.current && lastKeyPressed.current.code === KeyEvent.Tab && lastKeyPressed.current.shiftKey) {
          event.preventDefault();
          event.stopPropagation();
          // set focus back on toggle when leaving first or last element of collapsible
          toggleRef.current?.focus();
          setIsOpen(false);
        }
      };

      const setFocusToToggleLastElement = (event: Event) => {
        if (
          lastKeyPressed.current &&
          lastKeyPressed.current.code === KeyEvent.Tab &&
          !lastKeyPressed.current.shiftKey
        ) {
          event.preventDefault();
          event.stopPropagation();
          // set focus back on toggle when leaving first or last element of collapsible
          toggleRef.current?.focus();
          setIsOpen(false);
        }
      };

      focusableElements.at(0)?.addEventListener('blur', setFocusToToggleFirstElement);
      focusableElements.at(-1)?.addEventListener('blur', setFocusToToggleLastElement);

      return () => {
        focusableElements.at(0)?.removeEventListener('blur', setFocusToToggleFirstElement);
        focusableElements.at(-1)?.removeEventListener('blur', setFocusToToggleLastElement);
      };
    }
  }, [isOpen]);

  /**
   * For the collapsibles in ModalDialog, we need to add EventListener in modal-dialog node
   * as outside clickable node because modal-dialog is outside root node.
   * */
  useEffect(() => {
    let outsideClickNode = document.getElementById(GlobalDocumentIds.modalDialog);
    if (!outsideClickNode) {
      outsideClickNode = document.getElementById(GlobalDocumentIds.root);
    }

    if (isOpen && outsideClickNode) {
      outsideClickNode.addEventListener('click', onOutsideClick);
    }

    return () => {
      if (outsideClickNode) {
        outsideClickNode.removeEventListener('click', onOutsideClick);
      }
    };
  }, [isOpen, toggleCollapsible]);

  useEffect(() => {
    if (placement && isOpen && toggleRef.current && collapsibleRef.current) {
      popperInstance.current = createPopper(toggleRef.current, collapsibleRef.current, {
        placement,
        modifiers: [
          {
            name: 'preventOverflow',
            enabled: false,
          },
          {
            name: 'offset',
            options: {
              offset: [0, 15],
            },
          },
        ],
      });
    }

    return () => popperInstance.current && popperInstance.current.destroy();
  }, [isOpen, placement, collapsibleRefHeight]);

  useEffect(() => {
    setIsOpen(false);
  }, [location.key]);

  // Default props for a toggle component with correct aria attributes
  const toggleProps = {
    'aria-controls': id,
    'aria-expanded': isOpen,
    onClick: toggleCollapsible,
    onKeyDown,
    ref: toggleRef,
  };

  // Default props for a (i.e.) <CollapsibleContent /> component
  // which renders its children based on `isOpen`.
  // Optionally it takes a `className`.
  const collapsibleProps = {
    'aria-hidden': !isOpen,
    id,
    isOpen,
    onKeyDown,
    ref: collapsibleRef,
    tabIndex: -1,
  };

  return {
    closeCollapsible,
    openCollapsible,
    collapsibleOpen: isOpen,
    toggleCollapsible,
    focusToggle,
    toggleProps,
    collapsibleProps,
    popperInstance: popperInstance.current,
  };
}
